import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Shield, BarChart3, Settings2, Target } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { openPreferences } from '@/lib/cookieConsent';

const CookiesPolicy = () => {
  useEffect(() => {
    document.title = 'Cookies Policy — Abney Low Nursing Home';
    window.scrollTo(0, 0);
  }, []);

  const cookieCategories = [
    {
      icon: Shield,
      label: 'Strictly Necessary',
      badge: 'Always on',
      badgeClass: 'bg-emerald-50 border border-emerald-200 text-emerald-700',
      desc: 'These cookies are essential for the website to function — routing between pages, keeping the admin session secure, and remembering that you have already dismissed the cookie banner. They cannot be switched off.',
      examples: [
        { name: 'abneylow.cookieConsent.v1', purpose: 'Remembers your cookie preferences', duration: '12 months' },
        { name: 'session (admin)', purpose: 'Keeps the admin dashboard signed in', duration: 'Session only' },
      ],
    },
    {
      icon: BarChart3,
      label: 'Analytical & Performance',
      badge: 'Optional',
      badgeClass: 'bg-slate-50 border border-slate-200 text-slate-600',
      desc: 'These help us understand how visitors use the site (which pages are read most, how long people stay, where errors happen) so we can improve it.',
      examples: [
        { name: 'PostHog (ph_*)', purpose: 'Anonymous usage analytics (page views, click events)', duration: 'Up to 12 months' },
      ],
    },
    {
      icon: Settings2,
      label: 'Functionality',
      badge: 'Optional',
      badgeClass: 'bg-slate-50 border border-slate-200 text-slate-600',
      desc: 'These remember choices you make on the site (e.g. dismissing the live bed-availability widget or preferred text size) so you don\'t have to set them again.',
      examples: [
        { name: 'ui.prefs', purpose: 'Interface preferences', duration: '12 months' },
      ],
    },
    {
      icon: Target,
      label: 'Targeting & Advertising',
      badge: 'Not used',
      badgeClass: 'bg-slate-50 border border-slate-200 text-slate-500',
      desc: 'We do not currently use targeting or advertising cookies. This category is listed for transparency and future-proofing — if we ever begin using them we will update this page and ask for your consent.',
      examples: [],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main className="pt-32 pb-16" data-testid="cookies-policy-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#1e3a5f] hover:text-[#c9a227] font-semibold mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-[#c9a227]/15 border border-[#c9a227]/30 flex items-center justify-center shrink-0">
              <Cookie className="w-5 h-5 text-[#c9a227]" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1e3a5f]">Cookies Policy</h1>
          </div>
          <p className="text-sm text-slate-500 mb-8">Last updated: 2 April 2026 · Policy reference: GDPR08</p>

          <p className="text-slate-700 mb-6 text-[15px] leading-relaxed">
            Cookies are small text files a website places on your device when you visit. We use them to
            make the site work, remember your preferences and — with your consent — measure how the
            site is used. This policy sits alongside our{' '}
            <Link to="/privacy" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">Privacy Policy</Link>.
          </p>

          <div className="p-4 bg-[#1e3a5f]/5 border border-[#1e3a5f]/15 rounded-2xl mb-10 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
            <div className="text-sm text-slate-700">
              You can change your cookie preferences at any time.
            </div>
            <Button
              onClick={openPreferences}
              className="rounded-full bg-[#c9a227] hover:bg-[#d4b03a] text-[#1e3a5f] font-semibold text-sm px-5"
              data-testid="reopen-cookie-preferences-btn"
            >
              Manage cookie preferences
            </Button>
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-4">Categories of cookies we use</h2>
          <div className="space-y-4 mb-10">
            {cookieCategories.map((cat) => {
              const Icon = cat.icon;
              return (
                <div
                  key={cat.label}
                  className="border border-slate-200 rounded-2xl p-5"
                  data-testid={`cookies-cat-${cat.label.toLowerCase().replace(/[^a-z]/g, '-').replace(/-+/g, '-')}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-[#1e3a5f]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-bold text-[#1e3a5f]">{cat.label}</h3>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${cat.badgeClass}`}>
                          {cat.badge}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{cat.desc}</p>
                    </div>
                  </div>

                  {cat.examples.length > 0 && (
                    <div className="mt-3 overflow-x-auto rounded-lg border border-slate-100">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider">
                          <tr>
                            <th className="text-left px-3 py-2 font-semibold">Cookie / Tool</th>
                            <th className="text-left px-3 py-2 font-semibold">Purpose</th>
                            <th className="text-left px-3 py-2 font-semibold">Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cat.examples.map((c, i) => (
                            <tr key={i} className="border-t border-slate-100 text-slate-700">
                              <td className="px-3 py-2 font-mono">{c.name}</td>
                              <td className="px-3 py-2">{c.purpose}</td>
                              <td className="px-3 py-2">{c.duration}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <h2 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-3">Managing cookies in your browser</h2>
          <p className="text-slate-700 mb-4 text-[15px] leading-relaxed">
            You can also block or delete cookies through your browser settings. Note that if you block all
            cookies (including strictly necessary ones) parts of this site may stop working correctly.
          </p>
          <ul className="list-disc pl-6 space-y-1.5 text-slate-700 mb-10 text-[15px]">
            <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">Google Chrome</a></li>
            <li><a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">Safari</a></li>
            <li><a href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">Firefox</a></li>
            <li><a href="https://support.microsoft.com/en-gb/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">Microsoft Edge</a></li>
          </ul>

          <div className="p-5 bg-slate-50 border border-slate-200 rounded-2xl text-sm text-slate-700">
            <strong className="block text-[#1e3a5f] mb-1">Questions?</strong>
            Contact us at{' '}
            <a href="mailto:manager@abneylownursing.co.uk" className="text-[#1e3a5f] underline underline-offset-2 hover:text-[#c9a227] font-semibold">manager@abneylownursing.co.uk</a> or 0151 632 4504.
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CookiesPolicy;
